import { formatDistance as formatDistanceFns, formatISO } from 'date-fns';
import { isToday } from '../date-helpers/date-helpers';
import { _baseFormat } from '../shared/base-format';
/**
 * Return the formatted date string in the given format.
 *
 * @category Formatting helpers.
 *
 * @param {Date | string} date - The date to format.
 * @param {DateFormats | DateStyle} dateFormat - The formatting tokens.
 * @returns {string} - The formatted date string.
 */
export const formatDate = (date, dateFormat) => _baseFormat(date, dateFormat);
/**
 * Returns the month name from a date. Can either be the full name or shorthand name.
 *
 * @category Formatting helpers.
 *
 * @param {Date} date - The date object to use.
 * @param {boolean} shorthand - Return the shorthand version.
 * @returns {string} - The formatted month.
 */
export const getFormattedMonth = (date, shorthand = false) => {
    const dateFormat = shorthand ? 'LLL' : 'LLLL';
    return _baseFormat(date, dateFormat);
};
/**
 * Returns the formatted day of the month e.g. 1st, 2nd, ..., 31st
 *
 * @category Formatting helpers.
 *
 * @param {Date} date - The date object to use.
 * @returns {string} - The formatted day.
 */
export const getFormattedDay = (date) => _baseFormat(date, 'do');
/**
 * Formats the date in the format 1st March 2022 or shorthand 1st Mar 22.
 *
 * @category Formatting helpers.
 *
 * @param {Date} date - The date object to use.
 * @param {boolean} shorthand - Return the shorthand version of the month and year.
 * @returns {string} - The formatted date.
 */
export const getFormattedDate = (date, shorthand = false) => {
    const dayFormat = 'do';
    const monthFormat = shorthand ? 'LLL' : 'LLLL';
    const yearFormat = shorthand ? 'yy' : 'y';
    return _baseFormat(date, `${dayFormat} ${monthFormat} ${yearFormat}`);
};
/**
 * Calculates whether text should show 'today' or the formatted date
 *
 * @param {Date} date - The date to format.
 * @returns {string} - The formatted date or text for today.
 */
export const getFormattedDateText = (date, shorthand = false) => {
    const formattedDate = isToday(date)
        ? 'today'
        : getFormattedDate(date, shorthand);
    return formattedDate;
};
/**
 * Formats the date in ISO 8601 format.
 *
 * @param {Date} date - The date to format.
 * @returns {string} - The formatted date as an ISO 8601 format string.
 */
export const formatDateISO = (date) => formatISO(date);
/**
 * Return the distance between the given dates in words.
 *
 * @param {Date} date - The date.
 * @param {Date} baseDate - The date to compare with.
 * @param {Object} options - The optional object for options.
 * includeSeconds - distances less than a minute are more detailed.
 * addSuffix - result indicates if the second date is earlier or later than the first
 * @returns {string} - The distance in words.
 */
export const formatDistance = (date, baseDate, options) => formatDistanceFns(date, baseDate, { ...options });
//# sourceMappingURL=format-date.js.map